import { UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { AbstractSpaceClass } from '../../../../../models/space/abstract-space.class';
import { AbstractRequestClass } from '../details-overlay/request/abstract-request.class';
import { SuiteType } from '../item.model';
import type { DetailsOverlayComponent } from './details-overlay.component';
import { AvailabilityPropertyAssociationRequestClass } from './request/availability-property-association-request.class';
import { AvailabilityRequestClass } from './request/availability-request.class';
import { LeaseRequestClass } from './request/lease-request.class';
import { LeaseSpaceRequestClass } from './request/lease-space-request.class';
import { TenantRequestClass } from './request/tenant-request.class';
import { DataObject } from '../../../../../models/data-object.models';

export interface DetailsOverlayData<T extends AbstractSpaceClass = AbstractSpaceClass> {
  id?: (space: T) => string;
  title: (space: T) => string;
  navigateToEntity?: (space: T) => string;
  validators?: { groupName: string; validator: ValidatorFn[] }[];
  progressBar?: boolean;
  subsets: OverlayField<T>[];
  actions?: OverlayAction[];
  summaryLabels?: { label: string; fields: { label: string; displayFn: (record: Record<string, any>) => string }[] }[];
  formValidator?: ValidatorFn[];
  notesUpdate?: OverlayField<T>[];
  footer?: OverlayField<T>;
  remove?: boolean;
  removeIdInTitle?: boolean;
  disableMenu?(record: Record<string, any>): boolean;
}

export interface Title {
  field?: string;
  suiteType?: string;
  navigationLink?: string;
}

export interface NavigationObject<T extends AbstractSpaceClass = AbstractSpaceClass> {
  entity: SuiteType; // TODO: create entity enum and remove non-sense values from SuiteType
  getEntityField?: (space: T) => string | number;
}

export interface SubFieldConfig<T> {
  dataField?: string;
  inputType?: string;
  formGroup?: string;
  lookupCategory?: string;
  validators?: ValidatorFn;
  displayFn?: (space: T, ...args: any[]) => any | { [attr: string]: any };
  values?: any[];
  rawValueFn?: (space: T, ...args: any[]) => any | { [attr: string]: any };
}

export interface OverlayAction {
  label: string;
  inputType: string;
  actionFn: (...args: any) => void;
}

export interface OverlayField<T extends AbstractSpaceClass = AbstractSpaceClass> {
  id?: number;
  label?: string;
  dataField?: string;
  validators?: ValidatorFn[];
  formGroup?: string;
  unit?: SubFieldConfig<T>;
  estimated?: SubFieldConfig<T>;
  inputType?: string;
  lookupCategory?: string;
  displayFn: (space: T, ...args: any[]) => any | { [attr: string]: any };
  rawValueFn?: (space: T, ...args: any[]) => any | { [attr: string]: any };
  labelFn?: (columns: any) => any | { [attr: string]: any };
  plainValueFn?: (space: T) => string;
  navigateToEntity?: (space: T) => string;
  class?: string;
  navigationToEntity?: NavigationObject<T>;
  values?: any[];
  options?: { defaultTxt: string; label: string; rawData?: boolean };
  isLinkedOpportunities?: boolean;
  isRequired?: boolean;
  validationFn?: (space: T) => ValidatorFn;
  hideOnEdit?: boolean;
  isAreaField?: boolean;
}

export interface TenantRequestModel {
  tenantId: number;
  suiteNumber?: string;
  spaceId?: number;
  spaceSize?: number;
  spaceSizeUOM?: string;
  leaseStartDate?: string;
  leaseStartDateEstF?: boolean;
  leaseEndDate?: string;
  leaseEndDateEstF?: boolean;
  spaceStartDate?: string;
  spaceEndDate?: string;
  spaceOccupancyDate?: string;
  spaceLeaseStatus?: string;
  rentRate?: number;
  rentRateUOM?: string;
  industryNaicsId?: any;
  notes?: string;
  propertyId?: number;
  propertyUsageId?: number;
  floorNumber?: string;
  floorId: number;
  suiteId: number;
  lease?: Record<string, any>;
  full_floor_f?: boolean;
}

/**
 * DetailOverlayMenu
 * action: Method to use for click button
 * text: Text display in button, translate is default
 * class: Class to style button menu
 * disableTranslate: disble the translate for the text in button
 * disabled: disbable the button
 */
export interface DetailOverlayMenu {
  action: () => void;
  text: string;
  class?: string;
  disableTranslate?: boolean;
  disabled?: boolean;
  hide?: boolean;
}

export interface SPRequest {
  availability: DataObject;
  availabilitySpace: DataObject;
  tenantSpace: DataObject;
  tenant: DataObject;
  leaseSpace: DataObject;
  lease: DataObject;
}

export interface EntitySPRequestModel {
  entity: string;
  edpMethod: string;
  errorMessage: string;
  reqClass: AbstractRequestClass | any;
  entityKey: string;
}
export interface EntitySPOptions {
  availability: EntitySPRequestModel;
  availabilitySpace: EntitySPRequestModel;
  tenantSpace: EntitySPRequestModel;
  tenant: EntitySPRequestModel;
  leaseSpace: EntitySPRequestModel;
  lease: EntitySPRequestModel;
}

export enum EntitySPSpaces {
  Availability = 'availability',
  AvailabilitySpace = 'availabilitySpace',
  TenantSpace = 'tenantSpace',
  Tenant = 'tenant',
  LeaseSpace = 'leaseSpace',
  Lease = 'lease',
}

export const OPTIONS: EntitySPOptions = {
  availability: {
    entity: 'availability',
    edpMethod: 'updateAvailability',
    errorMessage: 'errorMessages.updateAvailability',
    reqClass: AvailabilityRequestClass,
    entityKey: 'id',
  },
  availabilitySpace: {
    entity: 'availability',
    edpMethod: 'updateAvailabilityPropertyAssociation',
    errorMessage: 'errorMessages.updateAvailability',
    reqClass: AvailabilityPropertyAssociationRequestClass,
    entityKey: 'id',
  },
  tenantSpace: {
    entity: 'tenancy',
    edpMethod: 'updateTenantPropertySpace',
    errorMessage: 'errorMessages.updateTenant',
    reqClass: TenantRequestClass,
    entityKey: 'tenantId',
  },
  tenant: {
    entity: 'tenancy',
    edpMethod: 'updateTenant',
    errorMessage: 'errorMessages.updateTenant',
    reqClass: TenantRequestClass,
    entityKey: 'tenantId',
  },
  leaseSpace: {
    entity: 'lease',
    edpMethod: 'miqUpdateLeaseArrangementPropertyFloorSuite',
    errorMessage: 'errorMessages.updateTenant',
    reqClass: LeaseSpaceRequestClass,
    entityKey: 'tenantId',
  },
  lease: {
    entity: 'lease',
    edpMethod: 'updateLeaseArrangement',
    errorMessage: 'errorMessages.updateTenant',
    reqClass: LeaseRequestClass,
    entityKey: 'tenantId',
  },
};

export interface UpgradedFormGroup extends UntypedFormGroup {
  component?: DetailsOverlayComponent;
  warnings?: ValidationErrors | null;
}

export interface OpportunitiesModel {
  opportunities: DataObject[];
  isAccessCIQ: boolean;
  message: string;
}
